import { Skeleton } from '@/components/ui'
import {
  useCreditBundles,
  useLoadingCreditBundles,
} from '../../creditBundlesStore'
import { useFetchCreditBundles } from '../../hooks'
import type { CreditBundle, Wallet } from '../../types'
import { BundleCard } from './BundleCard'
import { centsPerUnit } from './pricing'

const REFILL_COPY: Record<Wallet, string> = {
  pages:
    'Choose a refill package to add credits to your balance. Larger packages bring the cost per credit down.',
  screenings:
    'Choose a package to add screenings to your balance. Larger packages bring the cost per screening down.',
}

const bestValuePriceId = (wallet: Wallet, bundles: CreditBundle[]) => {
  if (bundles.length < 2) return null
  return bundles.reduce((best, bundle) =>
    centsPerUnit(wallet, bundle) < centsPerUnit(wallet, best) ? bundle : best,
  ).priceId
}

export const CreditBundleSelector = ({ wallet }: { wallet: Wallet }) => {
  useFetchCreditBundles(wallet)
  const bundles = useCreditBundles(wallet)
  const isLoading = useLoadingCreditBundles(wallet)

  if (bundles.length === 0 && !isLoading) return null

  const bestValue = bestValuePriceId(wallet, bundles)

  return (
    <div>
      <h3 className="text-lg font-semibold">Select a package</h3>
      <p className="text-muted-foreground max-w-lg text-sm mb-4">
        {REFILL_COPY[wallet]}
      </p>
      {isLoading && bundles.length === 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-56 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {bundles.map((bundle) => (
            <BundleCard
              key={bundle.priceId}
              wallet={wallet}
              bundle={bundle}
              isBestValue={bundle.priceId === bestValue}
            />
          ))}
        </div>
      )}
    </div>
  )
}
